/**
 * @file The level this device actually plays for one remote mic (#2223 AC44).
 *
 * Design context:
 * - **Measured at playback, not at the network.** With `webAudioMix`,
 *   LiveKit plays each remote track as source → (plugins) → GainNode →
 *   destination in the call's AudioContext. An AnalyserNode hung off that
 *   GainNode hears exactly what goes to the speakers: after the volume slider,
 *   and silent whenever the context is suspended/interrupted or the track
 *   delivers nothing. A second output on the gain node changes nothing about
 *   what plays.
 * - **Nothing is stored on the AudioContext or the track.** RoomOptions are
 *   compared as JSON, and a property on the context made LiveKit silently
 *   rejoin before; the analysers live in a WeakMap keyed by the GainNode, so a
 *   re-attach (new gain node) gets a new analyser and the old one is collected
 *   with its node (LiveKit's teardown disconnects all of the node's outputs).
 * - **Fallback** when a track is not on the gain path (iOS Speaker mode plays
 *   through plain <audio> elements, or a browser with no Web Audio): a
 *   paused/muted element or no element plays nothing (0); otherwise the
 *   receiver's `inbound-rtp.audioLevel`, which says what arrived, not what was
 *   heard -- a weaker signal, used only there.
 * - **Blind spots (AC47):** everything after the page -- the phone's silent
 *   switch, hardware volume, system/OS muting, a disconnected Bluetooth
 *   route, an output device that swallows audio -- cannot be seen here.
 */
import type { RemoteAudioTrack } from "livekit-client";
import { inboundAudioLevel } from "./callActivity";

interface Tap {
  analyser: AnalyserNode;
  data: Uint8Array<ArrayBuffer>;
}

const taps = new WeakMap<GainNode, Tap>();

/** LiveKit's private gain node for a webAudioMix track, if it is on that path. */
function gainNodeOf(track: RemoteAudioTrack): GainNode | undefined {
  return (track as unknown as { gainNode?: GainNode }).gainNode;
}

function tapFor(gain: GainNode): Tap | null {
  const existing = taps.get(gain);
  if (existing) return existing;
  try {
    const analyser = gain.context.createAnalyser();
    // The same scale as the mic analyser in VoicePathMonitor, so the floor
    // compares like with like.
    analyser.minDecibels = -90;
    analyser.maxDecibels = -10;
    analyser.smoothingTimeConstant = 0.5;
    analyser.fftSize = 1024;
    gain.connect(analyser);
    const tap = { analyser, data: new Uint8Array(analyser.frequencyBinCount) };
    taps.set(gain, tap);
    return tap;
  } catch {
    return null;
  }
}

/** RMS of the analyser's spectrum, 0-1 -- LiveKit's own `calculateVolume`. */
function volumeOf(tap: Tap): number {
  tap.analyser.getByteFrequencyData(tap.data);
  let sum = 0;
  for (const amplitude of tap.data) sum += (amplitude / 255) ** 2;
  return Math.sqrt(sum / tap.data.length);
}

/**
 * Synchronous played level for the gain path: a number, 0 when the context
 * is not running (nothing plays), or undefined when the track is not on the
 * gain path (use `fallbackPlaybackLevel`).
 */
export function gainPathLevel(track: RemoteAudioTrack): number | undefined {
  const gain = gainNodeOf(track);
  if (!gain) return undefined;
  if ((gain.context.state as string) !== "running") return 0;
  const tap = tapFor(gain);
  return tap ? volumeOf(tap) : undefined;
}

/** Played level when the track plays through an <audio> element; null = cannot tell. */
export async function fallbackPlaybackLevel(track: RemoteAudioTrack): Promise<number | null> {
  const elements = track.attachedElements;
  if (elements.length === 0) return 0;
  if (!elements.some((el) => !el.paused && !el.muted && el.volume > 0)) return 0;
  const receiver = track.receiver;
  if (!receiver?.getStats) return null;
  try {
    return inboundAudioLevel(await receiver.getStats());
  } catch {
    return null;
  }
}
